import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Text, Tooltip } from '@mantine/core';
import { type FormulaFunctions, evaluateFormula } from '@pst/shared';
import { formatAmount, formatQuantity } from '../format';

/**
 * A quantity that may be a formula - "2*(12,5+8)*3,1" or "BGF*0,35". Shows the
 * result; the formula is behind it, and while typing the result is previewed,
 * so a mistake shows before it is saved.
 */
export function FormulaCell({
  formula,
  value,
  variables,
  onCommit,
  readOnly,
  error,
  functions,
  money,
  empty = 'psch',
  emptyHint = 'leer = Pauschalbetrag',
  placeholder = 'z. B. 2*(12,5+8)*3 oder BGF*0,3',
}: {
  formula: string | null;
  value: number | null;
  variables: Record<string, number>;
  onCommit: (next: string | null) => void;
  readOnly?: boolean;
  error?: string | null;
  /** E.g. KG(300) for the preview while typing. */
  functions?: FormulaFunctions;
  /** Show the value as an amount (two decimals) rather than a quantity. */
  money?: boolean;
  /** Shown when there is no value. */
  empty?: string;
  emptyHint?: string;
  placeholder?: string;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(formula ?? '');
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (editing) {
      input.current?.focus();
      input.current?.select();
    }
  }, [editing]);
  const preview = draft.trim() ? evaluateFormula(draft, variables, functions) : null;
  const show = (v: number): string => (money ? formatAmount(v) : formatQuantity(v));
  const isFormula = formula !== null && !/^\s*\d+(?:[.,]\d+)?\s*$/.test(formula);

  if (editing && !readOnly) {
    return (
      <div style={{ position: 'relative' }}>
        <input
          ref={input}
          value={draft}
          onChange={(e) => setDraft(e.currentTarget.value)}
          onBlur={() => {
            setEditing(false);
            if ((draft.trim() || null) !== formula && (!preview || preview.ok)) onCommit(draft.trim() || null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (!preview || preview.ok)) {
              setEditing(false);
              if ((draft.trim() || null) !== formula) onCommit(draft.trim() || null);
            }
            if (e.key === 'Escape') {
              setDraft(formula ?? '');
              setEditing(false);
            }
          }}
          placeholder={placeholder}
          style={{
            width: '100%',
            border: `1px solid var(--mantine-color-${preview && !preview.ok ? 'red' : 'brand'}-5)`,
            borderRadius: 4,
            padding: '1px 4px',
            font: 'inherit',
            fontFamily: 'monospace',
            background: 'var(--mantine-color-body)',
            color: 'var(--mantine-color-text)',
          }}
        />
        <Text size="xs" c={preview && !preview.ok ? 'red.7' : 'dimmed'} style={{ position: 'absolute', top: '100%', left: 0, zIndex: 5, background: 'var(--mantine-color-body)', padding: '0 4px', whiteSpace: 'nowrap' }}>
          {preview ? (preview.ok ? `= ${show(preview.value)}` : preview.error) : emptyHint}
        </Text>
      </div>
    );
  }
  return (
    <Tooltip label={error ?? (isFormula ? `Ansatz: ${formula}` : 'Menge oder Formel eingeben')} disabled={readOnly && !isFormula && !error}>
      <Text
        size="sm"
        ta="right"
        c={error ? 'red.7' : value === null ? 'dimmed' : undefined}
        className={readOnly ? undefined : 'pst-cell'}
        role={readOnly ? undefined : 'button'}
        tabIndex={readOnly ? undefined : 0}
        onClick={() => {
          if (readOnly) return;
          setDraft(formula ?? '');
          setEditing(true);
        }}
        onKeyDown={(e) => {
          if (!readOnly && (e.key === 'Enter' || e.key === 'F2')) {
            e.preventDefault();
            setDraft(formula ?? '');
            setEditing(true);
          }
        }}
        style={{ whiteSpace: 'nowrap', minHeight: 20 }}
      >
        {error ? 'Fehler' : value === null ? empty : show(value)}
        {isFormula && !error && <Text span size="xs" c="dimmed"> ƒ</Text>}
      </Text>
    </Tooltip>
  );
}
